import { Link } from 'react-router-dom';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { Section, dataGridSx, dataGridDefaults } from '@/components/fleetguardian';
import { Badge } from '@/components/ui/badge';
import { useFraudHubCriticalBoard } from '@/api/hooks/useFraudHubCriticalBoard';
import { cn } from '@/lib/utils';
import { formatAlertTime, shortToken } from '@/lib/format';
import { SEVERITY_BADGE_LARGE_CLASS } from '@/lib/severity';
import type { TransactionKind } from '@/types/alerts';
import type { CriticalBoardAlert } from '@/types/fraudHub';
import { ExternalLink } from 'lucide-react';

const KIND_BADGE: Record<TransactionKind, string> = {
  fuel: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  oil: 'bg-amber-50 text-amber-700 border-amber-200',
  toll: 'bg-violet-50 text-violet-700 border-violet-200',
};

const columns: GridColDef<CriticalBoardAlert>[] = [
  {
    field: 'alert_time',
    headerName: 'Alert time',
    width: 160,
    valueFormatter: (value: string) => formatAlertTime(value),
  },
  {
    field: 'severity',
    headerName: 'Severity',
    width: 110,
    sortable: false,
    renderCell: () => (
      <Badge variant="outline" className={cn('font-medium', SEVERITY_BADGE_LARGE_CLASS.Critical)}>
        Critical
      </Badge>
    ),
  },
  {
    field: 'rule_id',
    headerName: 'Rule',
    width: 110,
    renderCell: (params) => (
      <span className="font-mono text-xs">{params.value as string}</span>
    ),
  },
  { field: 'risk_score', headerName: 'Risk', type: 'number', width: 80 },
  {
    field: 'registration',
    headerName: 'Asset',
    width: 130,
    valueGetter: (value: string | null, row: CriticalBoardAlert) =>
      value ?? shortToken(row.asset_token) ?? '—',
    renderCell: (params) => (
      <span className="font-mono text-xs">{params.value as string}</span>
    ),
  },
  {
    field: 'context',
    headerName: 'Station / Merchant',
    flex: 1,
    minWidth: 160,
    sortable: false,
    valueGetter: (_v, row: CriticalBoardAlert) =>
      row.station_name ?? row.merchant_name ?? '—',
  },
  {
    field: 'transaction_kind',
    headerName: 'Kind',
    width: 90,
    renderCell: (params) => {
      const kind = params.value as TransactionKind | null;
      return kind ? (
        <Badge variant="outline" className={cn('font-medium', KIND_BADGE[kind])}>
          {kind}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      );
    },
  },
  {
    field: 'actions',
    headerName: '',
    width: 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Link
        to={`/fraud/${params.row.id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        investigate
        <ExternalLink className="h-3 w-3" />
      </Link>
    ),
  },
];

/**
 * Fraud dashboard Critical board (Wave 2C) — the top-25 open Critical alerts
 * from `/fraud-hub/critical-board`, rendered as a DataGridPro (client-side
 * sort; period-INDEPENDENT because "open" is a status, not a window).
 */
export function CriticalBoardGrid(): JSX.Element {
  const { data, isLoading, isError, error } = useFraudHubCriticalBoard();
  const alerts = data?.alerts ?? [];
  const total = data?.total_critical_open ?? alerts.length;
  const truncated = total > alerts.length;

  return (
    <Section
      title="Critical Alerts Board"
      subtitle={
        truncated
          ? `Latest ${alerts.length} of ${total.toLocaleString('en-ZA')} open Critical`
          : 'Open Critical alerts — newest first'
      }
    >
      {isError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load Critical board: {error?.message ?? 'unknown error'}
        </div>
      ) : (
        <DataGridPro<CriticalBoardAlert>
          {...dataGridDefaults}
          rows={alerts}
          columns={columns}
          getRowId={(row) => row.id}
          loading={isLoading}
          hideFooter
          autoHeight
          sx={dataGridSx}
        />
      )}
    </Section>
  );
}

export default CriticalBoardGrid;
